import React, { useMemo, useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Sparkles,
  Lock,
  Loader2,
  Rocket,
  Pause,
  Play,
  Pencil,
  Trash2,
  ChevronRight,
  Flag,
  Undo2,
  ExternalLink,
} from 'lucide-react';
import { components, palette, withOpacity } from '../../../utils/designTokens';
import Pagination from '../../../components/Pagination';
import ClassifierWizard from './ClassifierWizard';
import { getDemoSignalDefinitions, getDemoSignalSummaryRows, getMockClassifierDimensions, getMockClassifierResults, getMockSignalSpikes } from '../../../services/mockDemoData';
import { BUILTIN_SIGNAL_DEFINITIONS } from '../../../services/builtinSignalDefinitions.generated';
import { v2ServicePath } from '../../../config';
import { useSessionJwt } from '../../../hooks/useSessionJwt';
import { signalsDetected } from '../../../utils/signalUtils';
import {
  classifierLabel,
  createClassifier,
  updateClassifier,
  deleteClassifier as deleteClassifierApi,
  setClassifierStatus,
  createClassifierLabel,
  deleteClassifierLabel,
  fetchClassifierLabels,
  fetchClassifierDefinitions,
  getClassifierEventDefinition,
} from '../../../services/classifiersApi';
import {
  getTableCellColor,
  getTableRowStyle,
  TableFrame,
  tableGridHeaderClassName,
} from '../../../components/TablePrimitives';

// =============================================================================
// Shared styles
// =============================================================================
// Grid column template: Type | Signals | Matches | Match Rate % | Latest | Version | Status | Traces
//
// The seven right-hand columns hold a count, a date or a badge, so they are sized
// to their content. Everything left over goes to the signal, whose description is
// the one value a reader is actually here to read: it previously shared the width
// as 2.4fr against 4.3fr of mostly empty numeric columns and truncated mid-word.
const gridColumns = 'minmax(280px, 1fr) minmax(56px, 72px) minmax(64px, 88px) minmax(78px, 104px) minmax(68px, 88px) minmax(50px, 64px) minmax(76px, 96px) minmax(62px, 78px)';


// Detail table grid (Trace ID | Time | Confidence | Reason | Action)
// Signal table grid (Signal | Matches | Description) — shared by its header and rows
const subtypeGridColumns = 'minmax(0, 1.05fr) 88px minmax(0, 2.2fr)';

// One signal is expanded at a time, so the list this disclosure controls needs
// only one id for aria-controls to point at.
const DETAIL_LIST_ID = 'signal-detail-list';

const SIGNAL_GROUPS = [
  { key: 'quality', label: 'Quality' },
  { key: 'agentic', label: 'Agentic' },
  { key: 'operational', label: 'Operational' },
  { key: 'safety', label: 'Safety' },
  { key: 'custom', label: 'Custom' },
  { key: 'other', label: 'Other' },
];

// =============================================================================
// Entity highlight renderer — renders [ENTITY_TYPE] tokens as colored badges
// =============================================================================
// =============================================================================
// Status badge
// =============================================================================
const STATUS_STYLES = {
  active: { label: 'Active', bg: withOpacity(palette.status.success, 12), color: palette.status.success },
  draft:  { label: 'Draft',  bg: palette.neutrals.surfaceMid, color: palette.text.muted },
  paused: { label: 'Paused', bg: withOpacity(palette.status.warning, 12), color: palette.status.warning },
};

const Badge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
};

// =============================================================================
// Status menu (dropdown actions)
// =============================================================================
const StatusMenu = ({ signal, onDeploy, onPause, onResume, onEdit, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const menuItem = (label, icon, color, onClick) => (
    <button
      key={label}
      type="button"
      onClick={() => { onClick(signal); onClose(); }}
      className="flex items-center gap-2 w-full px-3 py-1.5 text-xs rounded transition-colors text-left"
      style={{ color }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = palette.neutrals.surfaceHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {icon} {label}
    </button>
  );

  return (
    <div
      ref={ref}
      className="absolute z-20 py-1.5 rounded-lg"
      style={{
        top: 'calc(100% + 4px)',
        left: '50%',
        transform: 'translateX(-50%)',
        minWidth: '130px',
        backgroundColor: 'var(--color-dropdown-bg)',
        border: '1px solid var(--color-border-subtle)',
        boxShadow: 'var(--shadow-dropdown)',
      }}
    >
      {signal.status === 'draft' && menuItem('Deploy', <Rocket size={12} />, palette.status.success, onDeploy)}
      {signal.status === 'active' && menuItem('Pause', <Pause size={12} />, palette.status.warning, onPause)}
      {signal.status === 'paused' && menuItem('Resume', <Play size={12} />, palette.status.success, onResume)}
    </div>
  );
};

// =============================================================================
// Time formatting helper
// =============================================================================
const formatTime = (timestamp) => {
  if (!timestamp) return '-';
  try {
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '-';
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '-';
  }
};

// =============================================================================
// API helper
// =============================================================================
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const apiFetch = async (path, jwt, options = {}) => {
  const response = await fetch(`${apiUrl}${path}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${jwt}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${response.status}`);
  }
  return response.json();
};

const signalCategoryKey = (signal) => {
  if (signal?.category === 'custom') return 'custom';
  const classifierKey = `${signal?.classifier_key || ''}`.toLowerCase();
  const tags = Array.isArray(signal?.tags)
    ? signal.tags.map(tag => `${tag}`.toLowerCase())
    : [];
  if (classifierKey === 'error_detection' || classifierKey === 'workflow_anomaly' || tags.includes('operational') || tags.includes('anomaly')) return 'operational';
  if (classifierKey === 'loop_detection' || tags.includes('agentic')) return 'agentic';
  if (classifierKey === 'prompt_injection' || classifierKey === 'data_exfiltration' || tags.includes('safety')) return 'safety';
  if (classifierKey === 'hallucination' || tags.includes('quality')) return 'quality';
  return 'other';
};

// Row identity. classifier_id is not guaranteed (normalizeCustomClassifier can
// yield undefined), and comparing undefined to undefined reads as a match, so
// fall back to the key and never return an empty-ish value.
const signalRowKey = (signal) => (
  signal?.classifier_id || signal?.classifier_key || ''
);

// Two rows are the same signal only when they share a NON-EMPTY key, so rows
// that carry neither id nor key stay distinct instead of all matching at once.
const sameSignal = (a, b) => {
  const key = signalRowKey(a);
  return Boolean(key) && key === signalRowKey(b);
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const latestSignalSeen = (row, definition) => (
  row?.last_seen
  || row?.latest_seen
  || row?.updated_at
  || definition?.updated_at
  || definition?.created_at
  || ''
);

const normalizeDefinitionMetadata = (definition) => ({
  classifier_id: definition?.classifier_id || '',
  classifier_key: definition?.classifier_key || '',
  latest_version: definition?.classifier_version || definition?.latest_version || 1,
  name: definition?.name || classifierLabel(definition?.classifier_key),
  description: definition?.description || '',
  category: definition?.category || 'builtin',
  status: definition?.status || 'active',
  polarity: definition?.polarity || 'negative',
  tags: Array.isArray(definition?.tags) ? definition.tags : [],
  holistic: Boolean(definition?.holistic),
  subtypes: Array.isArray(definition?.subtypes) ? definition.subtypes : [],
  subtype_source_field: definition?.subtype_source_field || 'primary_subtype',
  match_type: definition?.match_type || '',
  created_at: definition?.created_at || '',
  updated_at: definition?.updated_at || '',
});

const mergeDefinitionAndCounts = (definition, countRow = {}) => {
  const metadata = normalizeDefinitionMetadata(definition || countRow);
  const classifierKey = metadata.classifier_key || countRow.classifier_key;
  return {
    classifier_id: metadata.category === 'custom'
      ? metadata.classifier_id
      : (metadata.classifier_id || `v2:${classifierKey}:v${metadata.latest_version || countRow.classifier_version || 1}`),
    classifier_key: classifierKey,
    latest_version: metadata.latest_version || countRow.classifier_version || 1,
    name: metadata.name || classifierLabel(classifierKey),
    description: metadata.description || countRow.description || '',
    category: metadata.category || countRow.category || 'builtin',
    status: metadata.status || countRow.status || 'active',
    polarity: metadata.polarity || countRow.polarity || 'negative',
    tags: metadata.tags.length > 0 ? metadata.tags : (Array.isArray(countRow.tags) ? countRow.tags : []),
    holistic: metadata.holistic,
    subtypes: metadata.subtypes,
    subtype_source_field: metadata.subtype_source_field,
    detected: toNumber(countRow.detected_count ?? countRow.detected),
    misclassified: toNumber(countRow.misclassified),
    total_evaluated: toNumber(countRow.eligible_count || countRow.trace_count || countRow.total_evaluated),
    latest_seen: latestSignalSeen(countRow, metadata),
    match_type: countRow.match_type || metadata.match_type || '',
    _source: metadata.category === 'custom' ? 'custom' : 'v2',
  };
};

const normalizeV2ClassifierDefinition = (row, definition = null) => mergeDefinitionAndCounts(definition || row, row);

const normalizeCustomClassifier = (row, definition = null) => ({
  ...mergeDefinitionAndCounts(definition || row, row),
  classifier_id: row.classifier_id || definition?.classifier_id,
  category: 'custom',
  _source: 'custom',
});

const buildSignalCatalogRows = (summaryRows, definitions) => {
  const summaryByKey = new Map();
  (Array.isArray(summaryRows) ? summaryRows : []).forEach((row) => {
    if (!row?.classifier_key) return;
    summaryByKey.set(row.classifier_key, row);
  });

  const rows = [];
  const seen = new Set();
  (Array.isArray(definitions) ? definitions : []).forEach((definition) => {
    if (!definition?.classifier_key) return;
    const summary = summaryByKey.get(definition.classifier_key) || {};
    rows.push(definition.category === 'custom'
      ? normalizeCustomClassifier({ ...summary, ...definition }, definition)
      : normalizeV2ClassifierDefinition(summary, definition));
    seen.add(definition.classifier_key);
  });

  summaryByKey.forEach((summary, key) => {
    if (seen.has(key)) return;
    rows.push(summary.category === 'custom'
      ? normalizeCustomClassifier(summary)
      : normalizeV2ClassifierDefinition(summary));
  });

  return rows.filter(row => row.classifier_key);
};

const collapseV2ClassifierDefinitions = (rows) => {
  const byKey = new Map();
  rows.forEach((row) => {
    if (!row?.classifier_key) return;
    const key = `${row.classifier_key}:v${row.classifier_version || 1}`;
    const current = byKey.get(key);
    if (!current) {
      byKey.set(key, { ...row, severity: row.severity || '', _severities: new Set([row.severity].filter(Boolean)) });
      return;
    }
    current.trace_count = Number(current.trace_count || 0) + Number(row.trace_count || 0);
    current.eligible_count = Number(current.eligible_count || 0) + Number(row.eligible_count || 0);
    current.detected_count = Number(current.detected_count || 0) + Number(row.detected_count || 0);
    current.clean_count = Number(current.clean_count || 0) + Number(row.clean_count || 0);
    current.not_applicable_count = Number(current.not_applicable_count || 0) + Number(row.not_applicable_count || 0);
    current.error_count = Number(current.error_count || 0) + Number(row.error_count || 0);
    current._severities.add(row.severity);
    current.severity = [...current._severities].filter(Boolean).join(', ');
  });
  return [...byKey.values()].map(row => {
    const { _severities, ...rest } = row;
    return rest;
  });
};

const formatClassifierDimension = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/^http_(\d+)$/, 'HTTP $1')
    .replace(/^http_other$/, 'HTTP other')
    .replace(/^tool_args_/, 'Tool args ')
    .replace(/^tool_schema_/, 'Tool schema ')
    .replace(/^tool_/, 'Tool ')
    .replace(/^exception_/, 'Exception ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

const normalizeV2ClassifierResult = (row) => ({
  id: row.id,
  classifier_result_id: row.id,
  trace_id: row.trace_id,
  span_id: row.primary_span_id || '',
  primary_span_id: row.primary_span_id || '',
  evidence_span_ids: Array.isArray(row.evidence_span_ids) ? row.evidence_span_ids : [],
  evidence: row.evidence || '',
  classifier_version: row.classifier_version || 1,
  detected: Boolean(row.detected),
  confidence: row.score,
  severity: row.severity || '',
  primary_subtype: row.primary_subtype || '',
  primary_event_key: row.primary_event_key || '',
  reason: row.reason,
  error: row.error || '',
  evaluated_at: row.evaluated_at,
  input_text: row.first_user_input || '',
  output_text: row.last_assistant_output || '',
  details: row.details || {},
});

const resultTypeLabel = (signal, result) => {
  if (!signal || !result) return '';
  const source = signal.subtype_source_field || 'primary_subtype';
  const subtypes = Array.isArray(signal.subtypes) ? signal.subtypes : [];
  const key = source === 'primary_event_key' ? result.primary_event_key : result.primary_subtype;
  const subtype = subtypes.find(item => item?.key === key);
  if (!subtype) return formatClassifierDimension(result.primary_event_key || result.primary_subtype);
  if (source === 'primary_event_key') return subtype.name || formatClassifierDimension(subtype.key);
  const eventDefinition = getClassifierEventDefinition(subtype, result.primary_event_key);
  const typeName = subtype.name || formatClassifierDimension(subtype.key);
  const eventName = eventDefinition?.name || (
    result.primary_event_key && result.primary_event_key !== subtype.key
      ? formatClassifierDimension(result.primary_event_key)
      : ''
  );
  return eventName ? `${typeName} · ${eventName}` : typeName;
};

const subtypeCountItem = (signal, subtype, dimensions) => {
  const key = subtype?.key || '';
  const source = signal?.subtype_source_field || 'primary_subtype';
  const list = source === 'primary_event_key'
    ? dimensions?.event_keys
    : dimensions?.subtypes;
  return (Array.isArray(list) ? list : []).find(item => item?.value === key) || null;
};

const eventCountItem = (subtypeKey, eventKey, dimensions) => {
  if (!subtypeKey || !eventKey) return null;
  const scoped = dimensions?.events_by_subtype?.[subtypeKey];
  const scopedMatch = (Array.isArray(scoped) ? scoped : []).find(item => item?.value === eventKey);
  if (scopedMatch) return scopedMatch;
  return (Array.isArray(dimensions?.event_keys) ? dimensions.event_keys : []).find(item => item?.value === eventKey) || null;
};

const liveEventRows = (signal, subtype, dimensions) => {
  if ((signal?.subtype_source_field || 'primary_subtype') === 'primary_event_key') return [];
  const declared = Array.isArray(subtype?.event_keys) ? subtype.event_keys : [];
  const patterns = Array.isArray(subtype?.event_key_patterns) ? subtype.event_key_patterns : [];
  const liveEvents = Array.isArray(dimensions?.events_by_subtype?.[subtype?.key])
    ? dimensions.events_by_subtype[subtype.key]
    : [];
  const declaredKeys = new Set(declared.map(item => item?.key).filter(Boolean));
  const rows = declared
    .filter(eventKey => eventKey?.key && eventKey.key !== subtype?.key)
    .map((eventKey, index) => {
      const live = eventCountItem(subtype.key, eventKey.key, dimensions);
      return {
        declaredOrder: index,
        key: eventKey.key,
        name: eventKey.name || formatClassifierDimension(eventKey.key),
        description: eventKey.description || '',
        detected_count: toNumber(live?.detected_count || live?.count),
        dynamic: false,
        live: Boolean(live),
      };
    });

  liveEvents.forEach((item) => {
    const key = item?.value || '';
    if (!key || key === subtype?.key || declaredKeys.has(key)) return;
    const eventDefinition = getClassifierEventDefinition(subtype, key);
    rows.push({
      declaredOrder: Infinity,
      key,
      name: eventDefinition?.name || formatClassifierDimension(key),
      description: eventDefinition?.description || '',
      detected_count: toNumber(item.detected_count || item.count),
      dynamic: Boolean(eventDefinition?.dynamic || patterns.length),
      live: true,
    });
  });

  if (rows.length === 0 && patterns.length > 0) {
    return patterns.map(pattern => ({
      key: pattern.key_prefix,
      name: pattern.name_template ? `${pattern.key_prefix}*` : formatClassifierDimension(pattern.key_prefix),
      description: pattern.description_template || '',
      detected_count: 0,
      dynamic: true,
      live: false,
      pattern: true,
    }));
  }

  // Declared first, in contract order. A live event the contract does not declare
  // has no position of its own, so it follows, ranked by how often it was seen.
  return rows.sort((a, b) => a.declaredOrder - b.declaredOrder
    || b.detected_count - a.detected_count
    || a.name.localeCompare(b.name));
};

const liveSubtypeRows = (signal, dimensions) => {
  const declared = Array.isArray(signal?.subtypes) ? signal.subtypes : [];
  const source = signal?.subtype_source_field || 'primary_subtype';
  const liveList = source === 'primary_event_key'
    ? dimensions?.event_keys
    : dimensions?.subtypes;
  const liveByValue = new Map((Array.isArray(liveList) ? liveList : [])
    .map(item => [item?.value, item])
    .filter(([key]) => key));
  const rows = declared.map((subtype) => {
    const live = subtypeCountItem(signal, subtype, dimensions);
    return {
      key: subtype.key,
      name: subtype.name || formatClassifierDimension(subtype.key),
      description: subtype.description || '',
      default_severity: subtype.default_severity || '',
      dynamic: Boolean(subtype.dynamic),
      detected_count: toNumber(live?.detected_count || live?.count),
      live: Boolean(live),
      event_rows: liveEventRows(signal, subtype, dimensions),
    };
  });
  liveByValue.forEach((item, key) => {
    if (declared.some(subtype => subtype.key === key)) return;
    rows.push({
      key,
      name: formatClassifierDimension(key),
      description: '',
      default_severity: '',
      dynamic: true,
      detected_count: toNumber(item.detected_count || item.count),
      live: true,
      event_rows: [],
    });
  });
  // Already in the order the contract declares them. Ranking by count here is
  // what put Unsupported Generation at the bottom of RAG Quality.
  return rows;
};

const MetadataBadge = ({ children, tone = 'neutral' }) => {
  const colors = tone === 'danger'
    ? { bg: withOpacity(palette.status.danger, 10), color: palette.status.danger }
    : tone === 'success'
      ? { bg: withOpacity(palette.status.success, 12), color: palette.status.success }
      : tone === 'warning'
        ? { bg: withOpacity(palette.status.warning, 12), color: palette.status.warning }
        : { bg: palette.neutrals.surfaceMid, color: palette.text.muted };
  return (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: colors.bg, color: colors.color }}>
      {children}
    </span>
  );
};

const SignalDetailBody = ({
  signal,
  timeLabel,
  detailTab,
  setDetailTab,
  detailListOpen,
  setDetailListOpen,
  detailResults,
  detailLoading,
  detailPagination,
  detailPage,
  setDetailPage,
  detailSubtype,
  detailEventKey,
  setDetailSubtype,
  setDetailEventKey,
  detailDimensions,
  detailDimensionsLoading,
  labelItems,
  labelsLoading,
  labelsPagination,
  labelsPage,
  setLabelsPage,
  onOpenTrace,
  onFlag,
  onUnflag,
  onEdit,
  onDelete,
  onDeploy,
  onPause,
  onResume,
  actionLoading,
}) => {
  const subtypeRows = useMemo(() => liveSubtypeRows(signal, detailDimensions), [signal, detailDimensions]);
  const isBuiltin = signal?.category === 'builtin';
  const canManage = signal && !isBuiltin && signal._source !== 'v2';

  const selectSubtype = (subtype) => {
    if (!subtype?.key) return;
    if (signal?.subtype_source_field === 'primary_event_key') {
      setDetailSubtype('');
      setDetailEventKey(subtype.key);
    } else {
      setDetailSubtype(subtype.key);
      setDetailEventKey('');
    }
    setDetailPage(1);
    setDetailTab('matches');
    setDetailListOpen(true);
  };

  return (
    <div
      className="px-5 py-4"
      style={{
        borderBottom: `1px solid ${palette.borders.subtle}`,
        borderLeft: components.table.row.selection.borderRight,
        backgroundColor: palette.neutrals.panelSecondary,
      }}
    >
      {!signal ? null : (
        <div className="flex flex-col gap-5">
          {canManage && (
            <div className="flex flex-wrap items-center gap-2">
              {signal.status === 'draft' && (
                <button type="button" disabled={actionLoading === signal.classifier_id} onClick={() => onDeploy?.(signal)} className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs ${components.button.primary}`}>
                  <Rocket size={12} /> Deploy
                </button>
              )}
              {signal.status === 'active' && (
                <button type="button" disabled={actionLoading === signal.classifier_id} onClick={() => onPause?.(signal)} className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs" style={{ borderColor: palette.borders.subtle, color: palette.status.warning }}>
                  <Pause size={12} /> Pause
                </button>
              )}
              {signal.status === 'paused' && (
                <button type="button" disabled={actionLoading === signal.classifier_id} onClick={() => onResume?.(signal)} className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs" style={{ borderColor: palette.borders.subtle, color: palette.status.success }}>
                  <Play size={12} /> Resume
                </button>
              )}
              <button type="button" onClick={() => onEdit?.(signal)} className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs" style={{ borderColor: palette.borders.subtle, color: palette.text.supporting }}>
                <Pencil size={12} /> Edit
              </button>
              <button type="button" onClick={() => onDelete?.(signal.classifier_id)} className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs" style={{ borderColor: withOpacity(palette.status.danger, 40), color: palette.status.danger }}>
                <Trash2 size={12} /> Delete
              </button>
            </div>
          )}

          <section>
            <div
              className="mb-2 grid items-center gap-3 px-3"
              style={{ gridTemplateColumns: subtypeGridColumns }}
            >
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold" style={{ color: palette.text.primary }}>Signals This Type Detects</h3>
                {detailDimensionsLoading && <Loader2 size={14} className="animate-spin" style={{ color: palette.text.muted }} />}
              </div>
              <div className={`${components.table.header.columnText} pr-4 text-right`}>Matches</div>
              <div className={components.table.header.columnText}>Description</div>
            </div>
            <div className="overflow-hidden rounded-md border" style={{ borderColor: palette.borders.subtle }}>
              {subtypeRows.length > 0 ? subtypeRows.map((subtype, index) => (
                <button
                  key={subtype.key}
                  type="button"
                  className="grid w-full items-start gap-3 px-3 py-2.5 text-left transition-opacity hover:opacity-90"
                  style={{
                    gridTemplateColumns: subtypeGridColumns,
                    borderBottom: index === subtypeRows.length - 1 ? 'none' : `1px solid ${palette.borders.subtle}`,
                    backgroundColor: subtype.key === detailSubtype || subtype.key === detailEventKey ? withOpacity(palette.accents.primary.base, 8) : palette.neutrals.panelPrimary,
                  }}
                  onClick={() => selectSubtype(subtype)}
                >
                  <div className="min-w-0">
                    <div className="truncate text-xs font-semibold" style={{ color: getTableCellColor('entity') }} title={subtype.name}>
                      {subtype.name}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {subtype.default_severity && <MetadataBadge tone={subtype.default_severity === 'critical' || subtype.default_severity === 'high' ? 'danger' : 'warning'}>{subtype.default_severity}</MetadataBadge>}
                      {subtype.dynamic && <MetadataBadge>dynamic</MetadataBadge>}
                    </div>
                  </div>
                  <div className="pr-4 text-right text-xs font-semibold tabular-nums" style={{ color: subtype.detected_count > 0 ? getTableCellColor('metric') : palette.text.muted }}>
                    {subtype.detected_count > 0 ? subtype.detected_count.toLocaleString() : '-'}
                  </div>
                  <div className="text-xs leading-5" style={{ color: palette.text.supporting }}>
                    <div>{subtype.description || 'Observed signal without a catalog description.'}</div>
                    {Array.isArray(subtype.event_rows) && subtype.event_rows.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {subtype.event_rows.slice(0, 5).map((eventRow) => (
                          <span
                            key={eventRow.key}
                            className="inline-flex max-w-full items-center gap-1 rounded px-1.5 py-0.5 text-[10px]"
                            style={{
                              backgroundColor: palette.neutrals.surfaceMid,
                              color: palette.text.muted,
                            }}
                            title={eventRow.description || eventRow.key}
                          >
                            <span className="truncate">{eventRow.name}</span>
                            {eventRow.dynamic && <span>dynamic</span>}
                            {eventRow.detected_count > 0 && (
                              <span className="tabular-nums" style={{ color: getTableCellColor('metric') }}>
                                {eventRow.detected_count.toLocaleString()}
                              </span>
                            )}
                          </span>
                        ))}
                        {subtype.event_rows.length > 5 && (
                          <span className="rounded px-1.5 py-0.5 text-[10px]" style={{ backgroundColor: palette.neutrals.surfaceMid, color: palette.text.muted }}>
                            +{subtype.event_rows.length - 5} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              )) : (
                <div className="px-3 py-4 text-sm" style={{ color: palette.text.muted }}>
                  This signal does not define fixed types.
                </div>
              )}
            </div>
          </section>

          <section>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex gap-0.5 rounded-md p-0.5" style={{ backgroundColor: palette.neutrals.surfaceMid }}>
                {[
                  { key: 'matches', label: `Matches (${detailPagination.total})`, noun: 'matching traces' },
                  ...(signal._source === 'v2' ? [] : [{ key: 'misclassified', label: `Labels (${labelsPagination.total})`, noun: 'labels' }]),
                ].map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => {
                      if (detailTab === tab.key) {
                        setDetailListOpen(open => !open);
                        return;
                      }
                      setDetailTab(tab.key);
                      setDetailListOpen(true);
                    }}
                    aria-expanded={detailTab === tab.key ? detailListOpen : false}
                    aria-controls={DETAIL_LIST_ID}
                    title={`${detailTab === tab.key && detailListOpen ? 'Hide' : 'Show'} ${tab.noun}`}
                    className="flex items-center gap-1 rounded px-2.5 py-1 text-[11px] font-medium"
                    style={{
                      backgroundColor: detailTab === tab.key ? palette.accents.primary.base : 'transparent',
                      color: detailTab === tab.key ? palette.text.inverse : palette.text.muted,
                    }}
                  >
                    <ChevronRight
                      size={11}
                      className="transition-transform"
                      style={{ transform: detailTab === tab.key && detailListOpen ? 'rotate(90deg)' : 'none' }}
                    />
                    {tab.label}
                  </button>
                ))}
              </div>
              {(detailSubtype || detailEventKey) && (
                <button
                  type="button"
                  onClick={() => {
                    setDetailSubtype('');
                    setDetailEventKey('');
                    setDetailPage(1);
                  }}
                  className="rounded px-2 py-1 text-[11px]"
                  style={{ color: palette.text.muted, backgroundColor: palette.neutrals.surfaceSubtle }}
                >
                  Clear signal
                </button>
              )}
            </div>

            <div id={DETAIL_LIST_ID}>
            {detailListOpen && (detailTab === 'matches' ? (
              detailLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={18} className="animate-spin" style={{ color: palette.text.muted }} />
                </div>
              ) : detailResults.length > 0 ? (
                <div className="overflow-hidden rounded-md border" style={{ borderColor: palette.borders.subtle }}>
                  {detailResults.map((result, index) => (
                    <button
                      key={`${result.trace_id}-${index}`}
                      type="button"
                      className="grid w-full items-center gap-3 px-3 py-2.5 text-left"
                    style={{
                        gridTemplateColumns: 'minmax(120px, 0.9fr) 76px minmax(0, 1.7fr) 58px',
                        borderBottom: index === detailResults.length - 1 ? 'none' : `1px solid ${palette.borders.subtle}`,
                        backgroundColor: palette.neutrals.panelPrimary,
                      }}
                      onClick={() => onOpenTrace?.(result)}
                    >
                      <span className="truncate font-mono text-xs" style={{ color: palette.accents.primary.base }} title={result.trace_id}>
                        {result.trace_id?.slice(0, 16) || '-'}
                      </span>
                      <span className="text-xs" style={{ color: palette.text.muted }}>{formatTime(result.evaluated_at)}</span>
                      <span className="truncate text-xs" style={{ color: palette.text.supporting }} title={result.reason || result.error || ''}>
                        {resultTypeLabel(signal, result) ? `${resultTypeLabel(signal, result)} · ` : ''}{result.reason || result.error || '-'}
                      </span>
                      <span className="flex items-center justify-end gap-1" onClick={(event) => event.stopPropagation()}>
                        {signal._source !== 'v2' && (
                          <button
                            type="button"
                            title="Mark as misclassified"
                            className="rounded p-1 transition-opacity hover:opacity-80"
                            style={{ color: palette.status.warning }}
                            onClick={() => onFlag?.(result)}
                          >
                            <Flag size={13} />
                          </button>
                        )}
                        <ExternalLink size={13} style={{ color: palette.text.muted }} />
                      </span>
                    </button>
                  ))}
                  <div className="px-3 py-3">
                    <Pagination
                      currentPage={detailPage}
                      itemsPerPage={DETAIL_PAGE_SIZE}
                      totalItems={detailPagination.total}
                      onPageChange={setDetailPage}
                      loading={detailLoading}
                      itemLabel="results"
                    />
                  </div>
                </div>
              ) : (
                <div className="rounded-md border px-3 py-6 text-center text-sm" style={{ borderColor: palette.borders.subtle, color: palette.text.muted }}>
                  No matches for this signal in {timeLabel || 'the selected range'}.
                </div>
              )
            ) : labelsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={18} className="animate-spin" style={{ color: palette.text.muted }} />
              </div>
            ) : labelItems.length > 0 ? (
              <div className="overflow-hidden rounded-md border" style={{ borderColor: palette.borders.subtle }}>
                {labelItems.map((item, index) => (
                  <div
                    key={item.label_id || index}
                    className="grid items-center gap-3 px-3 py-2.5"
                    style={{
                      gridTemplateColumns: 'minmax(120px, 0.9fr) 76px minmax(0, 1.8fr) 34px',
                      borderBottom: index === labelItems.length - 1 ? 'none' : `1px solid ${palette.borders.subtle}`,
                      backgroundColor: palette.neutrals.panelPrimary,
                    }}
                  >
                    <span className="truncate font-mono text-xs" style={{ color: palette.accents.primary.base }}>{item.trace_id?.slice(0, 16) || '-'}</span>
                    <span className="text-xs" style={{ color: palette.text.muted }}>{formatTime(item.evaluated_at)}</span>
                    <span className="truncate text-xs" style={{ color: palette.text.supporting }}>{item.reason || '-'}</span>
                    <button
                      type="button"
                      title="Undo misclassification"
                      className="rounded p-1 transition-opacity hover:opacity-80"
                      style={{ color: palette.text.supporting }}
                      onClick={() => onUnflag?.(item)}
                    >
                      <Undo2 size={13} />
                    </button>
                  </div>
                ))}
                <div className="px-3 py-3">
                  <Pagination
                    currentPage={labelsPage}
                    itemsPerPage={DETAIL_PAGE_SIZE}
                    totalItems={labelsPagination.total}
                    onPageChange={setLabelsPage}
                    loading={labelsLoading}
                    itemLabel="items"
                  />
                </div>
              </div>
            ) : (
              <div className="rounded-md border px-3 py-6 text-center text-sm" style={{ borderColor: palette.borders.subtle, color: palette.text.muted }}>
                No labels for this signal yet.
              </div>
            ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Main component
// =============================================================================
const DETAIL_PAGE_SIZE = 20;

const ClassifyTab = ({ appId, environment, timeLabel, selectedRange, defaultSignalKey, onDefaultSignalConsumed, isDemo = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, getJwt } = useSessionJwt();
  const [classifiers, setClassifiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState(null); // surfaced banner for failed signal actions
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingClassifier, setEditingClassifier] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [statusMenuOpenId, setStatusMenuOpenId] = useState('');

  // Detail panel state
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [scrollToSignalKey, setScrollToSignalKey] = useState('');
  const signalRowRefs = useRef({});
  const [detailTab, setDetailTab] = useState('matches');
  // The matching traces are a long list under a row the reader opened to read
  // the signal, so they start closed and open on the count beside them.
  const [detailListOpen, setDetailListOpen] = useState(false);
  const [detailResults, setDetailResults] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPagination, setDetailPagination] = useState({ limit: DETAIL_PAGE_SIZE, offset: 0, total: 0, has_more: false });
  const [detailPage, setDetailPage] = useState(1);
  const [detailSubtype, setDetailSubtype] = useState('');
  const [detailEventKey, setDetailEventKey] = useState('');
  const [detailDimensions, setDetailDimensions] = useState({ subtypes: [], event_keys: [], events_by_subtype: {} });
  const [detailDimensionsLoading, setDetailDimensionsLoading] = useState(false);
  const [labelItems, setLabelItems] = useState([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [labelsPagination, setLabelsPagination] = useState({ limit: DETAIL_PAGE_SIZE, offset: 0, total: 0, has_more: false });
  const [labelsPage, setLabelsPage] = useState(1);
  const [matchesDeltas, setMatchesDeltas] = useState({}); // local adjustments per classifier_key
  const [confirmFlag, setConfirmFlag] = useState(null); // result to confirm before labelling
  const [labelType, setLabelType] = useState('ignore'); // 'ignore' | 'misclassified'
  const [correctedClassifier, setCorrectedClassifier] = useState('');
  const [confirmUnflag, setConfirmUnflag] = useState(null); // label item to confirm before removing
  const [hoveredSignalId, setHoveredSignalId] = useState(null);

  // -- Fetch classifiers from API --
  const fetchClassifiers = useCallback(async () => {
    const jwt = getJwt();
    if (!jwt || !environment) return;

    try {
      setLoading(true);
      if (isDemo) {
        // Same builder the real path uses, fed the same two inputs: the
        // definitions a tenant would get from the API, and per-signal counts.
        // It used to enrich an empty list, so the tab was always empty.
        setClassifiers(buildSignalCatalogRows(
          getDemoSignalSummaryRows(appId),
          getDemoSignalDefinitions(),
        ));
        return;
      }

      const v2Params = new URLSearchParams();
      if (environment) v2Params.set('env', environment);
      if (selectedRange) v2Params.set('range', selectedRange);
      v2Params.set('limit', '100');
      v2Params.set('include_metadata', 'true');

      const [v2Data, definitionsData] = await Promise.all([
        apiFetch(
          `${v2ServicePath(appId, '/classifiers')}?${v2Params.toString()}`,
          jwt,
        ),
        fetchClassifierDefinitions(appId, jwt, environment),
      ]);
      const summaryRows = collapseV2ClassifierDefinitions(Array.isArray(v2Data.data) ? v2Data.data : []);
      const v2Classifiers = buildSignalCatalogRows(summaryRows, definitionsData);

      setClassifiers(v2Classifiers);
    } catch (err) {
      console.error('Failed to fetch classifiers:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId, getJwt, environment, appId, selectedRange, isDemo]);

  useEffect(() => { fetchClassifiers(); }, [fetchClassifiers]);

  // Auto-open wizard if "new=true" URL parameter is present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === 'true') {
      setWizardOpen(true);
      // Remove the parameter from URL after opening wizard
      params.delete('new');
      const newSearch = params.toString();
      navigate(`${location.pathname}${newSearch ? '?' + newSearch : ''}`, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // Auto-select a classifier when arriving from Overview signal spike click
  useEffect(() => {
    if (!defaultSignalKey || !classifiers.length || loading) return;
    const target = classifiers.find(c => c.classifier_key === defaultSignalKey);
    if (target) {
      setSelectedSignal(target);
      // A signal the reader did not open themselves starts closed, the same as
      // one they clicked. This is the only other path that selects a row.
      setDetailListOpen(false);
      setScrollToSignalKey(signalRowKey(target));
      onDefaultSignalConsumed?.();
    }
  }, [defaultSignalKey, classifiers, loading]);

  // Bring a deep-linked row into view once it has rendered. Without this the
  // expansion can open below the fold and the spike click reads as a no-op.
  useEffect(() => {
    if (!scrollToSignalKey) return;
    const node = signalRowRefs.current[scrollToSignalKey];
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setScrollToSignalKey('');
  }, [scrollToSignalKey, classifiers]);

  useEffect(() => {
    if (!selectedSignal || !classifiers.length) return;
    const updated = classifiers.find(c => sameSignal(c, selectedSignal));
    if (updated && updated !== selectedSignal) {
      setSelectedSignal(updated);
    }
  }, [classifiers, selectedSignal]);

  // -- Sort: recent spikes first, then non-zero matches descending, then zero --
  const sortedClassifiers = useMemo(() => {
    // In demo mode use spike keys to surface spiking signals first
    const spikeKeys = isDemo
      ? getMockSignalSpikes(appId).filter(s => s.key).map(s => s.key)
      : [];

    // Catalog order, not match count. This is a catalog: a reader learns where
    // each signal sits and finds it there next time. Ranking by matches reshuffled
    // it whenever the data moved, and put a signal with nothing found above one
    // with eleven as soon as a spike decayed.
    //
    // A spiking signal still floats to the top in demo mode, which is the one
    // case where recency beats a stable position.
    const catalogOrder = new Map(
      BUILTIN_SIGNAL_DEFINITIONS.map((d, index) => [d.classifier_key, index]),
    );
    const rank = (row) => {
      const position = catalogOrder.get(row.classifier_key);
      // A custom signal has no catalog position; it sorts after the built-ins,
      // which is also where its own section sits.
      return position === undefined ? BUILTIN_SIGNAL_DEFINITIONS.length : position;
    };

    return [...classifiers].sort((a, b) => {
      const aSpike = spikeKeys.indexOf(a.classifier_key);
      const bSpike = spikeKeys.indexOf(b.classifier_key);
      if (aSpike !== -1 || bSpike !== -1) {
        if (aSpike === -1) return 1;
        if (bSpike === -1) return -1;
        return aSpike - bSpike;
      }
      return rank(a) - rank(b) || (a.name || '').localeCompare(b.name || '');
    });
  }, [classifiers, isDemo, appId]);

  const groupedClassifiers = useMemo(() => {
    const grouped = new Map(SIGNAL_GROUPS.map(group => [group.key, []]));
    sortedClassifiers.forEach((signal) => {
      const groupKey = signalCategoryKey(signal);
      if (!grouped.has(groupKey)) grouped.set(groupKey, []);
      grouped.get(groupKey).push(signal);
    });
    return SIGNAL_GROUPS
      .map(group => ({ ...group, signals: grouped.get(group.key) || [] }))
      .filter(group => group.signals.length > 0);
  }, [sortedClassifiers]);

  const catalogRows = useMemo(() => (
    groupedClassifiers.flatMap(group => [
      {
        type: 'group',
        key: `group:${group.key}`,
        label: group.label,
        // A row in this group is a type; what it detects is the signal. So the
        // header counts the rows as types and their leaves as signals.
        typeCount: group.signals.length,
        signalCount: group.signals.reduce((total, signal) => total + signalsDetected(signal), 0),
      },
      ...group.signals.map(signal => ({ type: 'signal', key: signal.classifier_id, signal })),
    ])
  ), [groupedClassifiers]);

  // -- Fetch detail results (detected only) --
  const fetchResults = useCallback(async (classifierKey, page, subtype = '', eventKey = '') => {
    const jwt = getJwt();
    if (!jwt || !environment) return;
    setDetailLoading(true);

    try {
      const offset = (page - 1) * DETAIL_PAGE_SIZE;

      if (isDemo) {
        const data = getMockClassifierResults(classifierKey, appId);
        const results = data.results || [];
        const pagination = data.pagination || { limit: DETAIL_PAGE_SIZE, offset: 0, total: 0, has_more: false };
        setDetailResults(results);
        setDetailPagination(pagination);
        return;
      }

      const v2Params = new URLSearchParams({
        status: 'detected',
        limit: String(DETAIL_PAGE_SIZE),
        offset: String(offset),
      });
      if (environment) v2Params.set('env', environment);
      if (selectedRange) v2Params.set('range', selectedRange);
      if (subtype) v2Params.set('subtype', subtype);
      if (eventKey) v2Params.set('event_key', eventKey);
      const data = await apiFetch(
        `${v2ServicePath(appId, `/classifiers/${encodeURIComponent(classifierKey)}/traces`)}?${v2Params.toString()}`,
        jwt,
      );
      const rows = Array.isArray(data.data) ? data.data : [];
      const meta = data.meta || {};
      setDetailResults(rows.map(normalizeV2ClassifierResult));
      setDetailPagination({
        limit: Number(meta.limit ?? DETAIL_PAGE_SIZE),
        offset: Number(meta.offset ?? offset),
        total: Number(meta.total ?? rows.length),
        has_more: Boolean(meta.has_more),
      });
    } catch (err) {
      console.error('Failed to fetch classifier results:', err);
      setDetailResults([]);
      setDetailPagination({ limit: DETAIL_PAGE_SIZE, offset: 0, total: 0, has_more: false });
    } finally {
      setDetailLoading(false);
    }
  }, [sessionId, getJwt, environment, appId, selectedRange, isDemo]);

  const fetchDimensions = useCallback(async (classifierKey) => {
    const jwt = getJwt();
    if (!classifierKey) {
      setDetailDimensions({ subtypes: [], event_keys: [], events_by_subtype: {} });
      return;
    }
    // Demo mode has no endpoint behind it, and returning empty here is what left
    // the Matches column blank on every sub-type while the signal above it
    // reported matches.
    if (isDemo) {
      setDetailDimensions(getMockClassifierDimensions(classifierKey, appId));
      return;
    }
    if (!jwt || !environment) {
      setDetailDimensions({ subtypes: [], event_keys: [], events_by_subtype: {} });
      return;
    }

    setDetailDimensionsLoading(true);
    try {
      const params = new URLSearchParams({
        status: 'detected',
        limit: '500',
      });
      if (environment) params.set('env', environment);
      if (selectedRange) params.set('range', selectedRange);
      const data = await apiFetch(
        `${v2ServicePath(appId, `/classifiers/${encodeURIComponent(classifierKey)}/dimensions`)}?${params.toString()}`,
        jwt,
      );
      const payload = data.data || {};
      setDetailDimensions({
        subtypes: Array.isArray(payload.subtypes) ? payload.subtypes : [],
        event_keys: Array.isArray(payload.event_keys) ? payload.event_keys : [],
        events_by_subtype: payload.events_by_subtype || {},
      });
    } catch (err) {
      console.error('Failed to fetch classifier dimensions:', err);
      setDetailDimensions({ subtypes: [], event_keys: [], events_by_subtype: {} });
    } finally {
      setDetailDimensionsLoading(false);
    }
  }, [sessionId, getJwt, environment, selectedRange, appId, isDemo]);

  // -- Fetch labels (misclassified / missed items) --
  const fetchLabels = useCallback(async (classifierKey, page) => {
    const jwt = getJwt();
    if (!jwt || !environment) return;
    if (selectedSignal?._source === 'v2') {
      setLabelItems([]);
      setLabelsPagination({ limit: DETAIL_PAGE_SIZE, offset: 0, total: 0, has_more: false });
      return;
    }
    setLabelsLoading(true);

    try {
      const offset = (page - 1) * DETAIL_PAGE_SIZE;
      const data = await fetchClassifierLabels(appId, jwt, environment, {
        classifierType: classifierKey,
        limit: DETAIL_PAGE_SIZE,
        offset,
      });
      setLabelItems(data.labels || []);
      setLabelsPagination(data.pagination || { limit: DETAIL_PAGE_SIZE, offset: 0, total: 0, has_more: false });
    } catch (err) {
      console.error('Failed to fetch labels:', err);
      setLabelItems([]);
      setLabelsPagination({ limit: DETAIL_PAGE_SIZE, offset: 0, total: 0, has_more: false });
    } finally {
      setLabelsLoading(false);
    }
  }, [sessionId, getJwt, environment, appId, selectedSignal]);

  // Fetch matches when selection or page changes
  useEffect(() => {
    if (!selectedSignal) return;
    fetchResults(selectedSignal.classifier_key, detailPage, detailSubtype, detailEventKey);
  }, [selectedSignal, detailPage, detailSubtype, detailEventKey, fetchResults]);

  useEffect(() => {
    if (!selectedSignal || selectedSignal._source !== 'v2') {
      setDetailDimensions({ subtypes: [], event_keys: [], events_by_subtype: {} });
      return;
    }
    fetchDimensions(selectedSignal.classifier_key);
  }, [selectedSignal, fetchDimensions]);

  // Fetch labels when selection or labels page changes
  useEffect(() => {
    if (!selectedSignal) return;
    fetchLabels(selectedSignal.classifier_key, labelsPage);
  }, [selectedSignal, labelsPage, fetchLabels]);

  // -- Flag as misclassified --
  const handleFlag = useCallback(async (result) => {
    if (!selectedSignal) return;
    if (selectedSignal._source === 'v2') return;
    const resultId = result.id || result.classifier_result_id || `${result.trace_id}-${result.span_id}`;
    const classifierKey = selectedSignal.classifier_key;

    // Build label item from the match
    const labelItem = {
      label_id: `fb-local-${Date.now()}`,
      classifier_result_id: resultId,
      label_type: 'misclassified',
      corrected_classifier: result._correctedClassifier || '',
      trace_id: result.trace_id,
      span_id: result.span_id || '',
      confidence: result.confidence,
      reason: result.reason,
      evaluated_at: result.evaluated_at,
      labelled_at: new Date().toISOString(),
      labelled_by: 'user',
    };

    // Move item: remove from matches, add to labels
    setDetailResults(prev => prev.filter(r => {
      const rId = r.id || r.classifier_result_id || `${r.trace_id}-${r.span_id}`;
      return rId !== resultId;
    }));
    setDetailPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    setLabelItems(prev => [labelItem, ...prev]);
    setLabelsPagination(prev => ({ ...prev, total: prev.total + 1 }));
    setMatchesDeltas(prev => ({ ...prev, [classifierKey]: (prev[classifierKey] || 0) - 1 }));

    // Persist the label via the v2 labels API (service from path, env from ?env=).
    try {
      const body = {
        classifier_result_id: resultId,
        classifier_type: classifierKey,
        classifier_version: result.classifier_version || 1,
        label_type: 'misclassified',
        trace_id: result.trace_id,
        span_id: result.span_id || '',
        confidence: result.confidence,
        reason: result.reason,
        evaluated_at: result.evaluated_at,
        input_text: result.input_text || '',
        output_text: result.output_text || '',
      };
      if (result._correctedClassifier) {
        body.corrected_classifier = result._correctedClassifier;
      }
      await createClassifierLabel(appId, getJwt(), environment, body);
    } catch (err) {
      console.error('Failed to save label:', err);
      setActionError(`Couldn't save the label: ${err.message}`);
    }
  }, [getJwt, selectedSignal, environment, appId]);

  // -- Undo misclassification --
  const handleUnflag = useCallback(async (labelItem) => {
    if (!selectedSignal) return;
    if (selectedSignal._source === 'v2') return;
    const classifierKey = selectedSignal.classifier_key;

    // Move item: remove from labels, add back to matches
    const matchItem = {
      trace_id: labelItem.trace_id,
      span_id: labelItem.span_id || '',
      id: labelItem.classifier_result_id,
      classifier_result_id: labelItem.classifier_result_id,
      detected: true,
      confidence: labelItem.confidence,
      reason: labelItem.reason,
      evaluated_at: labelItem.evaluated_at,
    };

    setLabelItems(prev => prev.filter(f => f.label_id !== labelItem.label_id));
    setLabelsPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    setDetailResults(prev => [matchItem, ...prev]);
    setDetailPagination(prev => ({ ...prev, total: prev.total + 1 }));
    setMatchesDeltas(prev => ({ ...prev, [classifierKey]: (prev[classifierKey] || 0) + 1 }));

    // Remove the label via the v2 labels API.
    try {
      await deleteClassifierLabel(appId, getJwt(), environment, labelItem.label_id);
    } catch (err) {
      console.error('Failed to remove label:', err);
      setActionError(`Couldn't remove the label: ${err.message}`);
    }
  }, [getJwt, selectedSignal, appId, environment]);

  // -- Row click handler --
  const handleRowClick = useCallback((signal) => {
    // Accordion: clicking the open row collapses it.
    setSelectedSignal(prev => (sameSignal(prev, signal) ? null : signal));
    setDetailPage(1);
    setLabelsPage(1);
    setDetailTab('matches');
    setDetailListOpen(false);
    setDetailSubtype('');
    setDetailEventKey('');
    setDetailResults([]);
    setLabelItems([]);
    setDetailPagination({ limit: DETAIL_PAGE_SIZE, offset: 0, total: 0, has_more: false });
    setLabelsPagination({ limit: DETAIL_PAGE_SIZE, offset: 0, total: 0, has_more: false });
    setDetailDimensions({ subtypes: [], event_keys: [], events_by_subtype: {} });
  }, []);

  const handleOpenTrace = useCallback((result) => {
    if (!result?.trace_id) return;
    const params = new URLSearchParams({ env: environment });
    navigate(`/app/${appId}/traces/${result.trace_id}?${params.toString()}`, {
      state: { referrerUrl: window.location.pathname + window.location.search },
    });
  }, [appId, environment, navigate]);

  // `subtype`/`eventKey` are passed in rather than read from the shared detail
  // slice: every row has a Traces button now, and that slice belongs to whichever
  // row is expanded — reading it here sent row B's link out with row A's filter.
  const handleViewAllTraces = useCallback((signal, { subtype = '', eventKey = '' } = {}) => {
    if (!signal?.classifier_key) return;
    const params = new URLSearchParams();
    if (environment) params.set('env', environment);
    if (selectedRange) params.set('range', selectedRange);
    params.set('classifier', signal.classifier_key);
    params.set('classifier_status', 'detected');
    if (subtype) params.set('subtype', subtype);
    if (eventKey) params.set('event_key', eventKey);
    params.set('from', 'signals-definition');
    navigate(`/app/${appId}/traces?${params.toString()}`);
  }, [appId, environment, navigate, selectedRange]);

  // -- Actions --
  const callAction = useCallback(async (classifierId, action) => {
    if (!getJwt()) return;
    if (String(classifierId).startsWith('v2:')) return;
    setActionLoading(classifierId);
    setActionError(null);
    try {
      await setClassifierStatus(appId, getJwt(), environment, classifierId, action);
      await fetchClassifiers();
    } catch (err) {
      console.error(`Failed to ${action} classifier:`, err);
      setActionError(`Couldn't ${action} the signal: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  }, [getJwt, appId, environment, fetchClassifiers]);

  const handleDeploy = useCallback((c) => callAction(c.classifier_id, 'deploy'), [callAction]);
  const handlePause = useCallback((c) => callAction(c.classifier_id, 'pause'), [callAction]);
  const handleResume = useCallback((c) => callAction(c.classifier_id, 'resume'), [callAction]);

  const handleEdit = useCallback((c) => {
    if (c?._source === 'v2') return;
    setEditingClassifier(c);
    setWizardOpen(true);
    setStatusMenuOpenId('');
  }, []);

  const handleDeleteConfirmed = useCallback(async () => {
    if (!getJwt() || !confirmDeleteId) return;
    if (String(confirmDeleteId).startsWith('v2:')) return;
    const deleteId = confirmDeleteId;
    setActionLoading(deleteId);
    setConfirmDeleteId(null);
    setActionError(null);
    try {
      await deleteClassifierApi(appId, getJwt(), environment, deleteId);
      await fetchClassifiers();
    } catch (err) {
      console.error('Failed to delete classifier:', err);
      setActionError(`Couldn't delete the signal: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  }, [getJwt, confirmDeleteId, appId, environment, fetchClassifiers]);

  const handleCreate = useCallback(() => {
    setEditingClassifier(null);
    setWizardOpen(true);
  }, []);

  const handleSave = useCallback(async (data) => {
    const jwt = getJwt();
    if (!jwt) throw new Error('Not authenticated');
    try {
      if (editingClassifier) {
        await updateClassifier(appId, jwt, environment, editingClassifier.classifier_id, data);
      } else {
        await createClassifier(appId, jwt, environment, data);
      }
      await fetchClassifiers();
    } catch (err) {
      console.error('Failed to save classifier:', err);
      throw err; // re-thrown so the wizard surfaces it inline and stays open
    }
  }, [getJwt, editingClassifier, appId, environment, fetchClassifiers]);

  // -- Loading state (initial load only — don't unmount during background refreshes) --
  if (loading && classifiers.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin" style={{ color: palette.text.muted }} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-8">

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: palette.text.heading }}>
            Signals
          </h1>
        </div>
        <button
          onClick={handleCreate}
          className={`flex items-center gap-2 px-4 py-2 text-sm flex-shrink-0 rounded-lg ${components.button.primary}`}
        >
          <Sparkles size={14} />
          New signal
        </button>
      </div>

      {/* Action error banner (deploy/pause/resume/delete/label failures) */}
      {actionError && (
        <div
          className="flex items-start justify-between gap-3 p-3 rounded-lg text-sm"
          style={{
            backgroundColor: withOpacity(palette.status.danger, 8),
            border: `1px solid ${withOpacity(palette.status.danger, 20)}`,
            color: palette.status.danger,
          }}
        >
          <span>{actionError}</span>
          <button
            onClick={() => setActionError(null)}
            className="flex-shrink-0 font-medium"
            style={{ color: palette.status.danger }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Signals table */}
      <TableFrame style={{ overflow: 'visible' }}>

        {/* Table header */}
        <div
          className={tableGridHeaderClassName}
          style={{
            gridTemplateColumns: gridColumns,
            columnGap: '12px',
          }}
        >
          <div>
            Type
          </div>
          <div className="text-center">
            Signals
          </div>
          <div className="text-center">
            Matches
          </div>
          <div className="text-center">
            Match Rate %
          </div>
          <div>
            Latest
          </div>
          <div className="text-center">
            Version
          </div>
          <div className="text-center">
            Status
          </div>
          <div className="text-center">
            Traces
          </div>
        </div>

        {/* Table rows */}
        {catalogRows.length > 0 ? (
          catalogRows.map((entry, index) => {
            if (entry.type === 'group') {
              return (
                <div
                  key={entry.key}
                  className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider"
                  style={{
                    color: palette.text.supporting,
                    backgroundColor: palette.neutrals.surfaceSubtle,
                    borderTop: index === 0 ? 'none' : `1px solid ${palette.borders.subtle}`,
                    borderBottom: `1px solid ${palette.borders.subtle}`,
                  }}
                >
                  {entry.label}
                  <span className="ml-2 font-normal normal-case tracking-normal" style={{ color: palette.text.muted }}>
                    {entry.typeCount} type{entry.typeCount === 1 ? '' : 's'}
                    {` \u00b7 ${entry.signalCount} signal${entry.signalCount === 1 ? '' : 's'}`}
                  </span>
                </div>
              );
            }
            const signal = entry.signal;
            const isBuiltin = signal.category === 'builtin';
            const isSelected = sameSignal(selectedSignal, signal);
            const matches = (signal.detected != null ? signal.detected : 0) + (matchesDeltas[signal.classifier_key] || 0);
            const evaluated = toNumber(signal.total_evaluated);
            // Deliberate deviation from console/src/CLAUDE.md's "true zero shows 0":
            // Match Rate is gated on `matches > 0` so it dashes in lockstep with the
            // Matches cell beside it. A row reading "- / 0.0%" was the confusing part.
            const matchRate = matches > 0 && evaluated > 0 ? ((matches / evaluated) * 100).toFixed(1) : null;
            const subtypeCount = signalsDetected(signal);

            return (
              <React.Fragment key={entry.key}>
                <div
                  ref={(node) => { signalRowRefs.current[signalRowKey(signal)] = node; }}
                  className={`grid items-center px-3 py-3 transition-colors ${components.table.row.border}`}
                  style={getTableRowStyle(index, {
                    isHovered: hoveredSignalId === signal.classifier_id,
                    isSelected,
                    interactive: true,
                    selectedEdge: 'left',
                    style: {
                      gridTemplateColumns: gridColumns,
                      columnGap: '12px',
                    },
                  })}
                  onClick={() => handleRowClick(signal)}
                  onMouseEnter={() => setHoveredSignalId(signal.classifier_id)}
                  onMouseLeave={() => setHoveredSignalId(null)}
                >
                  {/* Signal name + description */}
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <ChevronRight
                        size={12}
                        className="flex-shrink-0 transition-transform"
                        style={{
                          color: palette.text.muted,
                          transform: isSelected ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      />
                      <span className="min-w-0 truncate text-sm font-semibold" style={{ color: getTableCellColor('entity') }}>
                        {signal.name}
                      </span>
                      {isBuiltin && (
                        <span
                          className="flex items-center justify-center flex-shrink-0 rounded-full"
                          style={{
                            width: '18px',
                            height: '18px',
                            backgroundColor: palette.neutrals.surfaceMid,
                          }}
                          title="Built-in signal"
                        >
                          <Lock size={10} style={{ color: palette.text.muted }} />
                        </span>
                      )}
                      <MetadataBadge tone={signal.polarity === 'positive' ? 'success' : 'danger'}>
                        {signal.polarity || 'negative'}
                      </MetadataBadge>
                    </div>
                    {/* Two lines, so most descriptions show in full, with the
                        title carrying the rest when one still does not fit. */}
                    <span
                      className="text-xs ml-5 overflow-hidden"
                      style={{
                        color: getTableCellColor('supporting'),
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                      title={signal.description || ''}
                    >
                      {signal.description || '-'}
                    </span>
                  </div>

                  {/* Signals */}
                  <div className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <span className="text-sm" style={{ color: getTableCellColor('metric') }}>
                      {subtypeCount.toLocaleString()}
                    </span>
                  </div>

                  {/* Matches */}
                  <div className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <span className="text-sm font-medium" style={{ color: matches > 0 ? getTableCellColor('metric') : getTableCellColor('muted') }}>
                      {matches > 0 ? matches.toLocaleString() : '-'}
                    </span>
                  </div>

                  {/* Match Rate % */}
                  <div
                    className="text-center"
                    style={{ fontVariantNumeric: 'tabular-nums', cursor: matchRate != null ? 'help' : undefined }}
                    title={matchRate != null ? `${matches.toLocaleString()} of ${evaluated.toLocaleString()} traces evaluated` : undefined}
                  >
                    <span className="text-sm" style={{ color: matchRate != null ? getTableCellColor('metric') : getTableCellColor('muted') }}>
                      {matchRate != null ? `${matchRate}%` : '-'}
                    </span>
                  </div>

                  {/* Latest */}
                  <div className="min-w-0">
                    <span className="truncate text-xs" style={{ color: getTableCellColor('time') }} title={signal.latest_seen || ''}>
                      {formatTime(signal.latest_seen)}
                    </span>
                  </div>

                  {/* Version */}
                  <div className="flex justify-center">
                    <span
                      className="px-2 py-0.5 rounded-full text-[11px] font-semibold"
                      style={{ backgroundColor: palette.neutrals.surfaceMid, color: getTableCellColor('supporting') }}
                    >
                      v{signal.latest_version || 1}
                    </span>
                  </div>

                  {/* Status */}
                  <div className="flex items-center justify-center gap-2 relative" onClick={(e) => e.stopPropagation()}>
                    {actionLoading === signal.classifier_id ? (
                      <Loader2 size={14} className="animate-spin" style={{ color: palette.text.muted }} />
                    ) : isBuiltin || signal._source === 'v2' ? (
                      <Badge status={signal.status || 'active'} />
                    ) : (
                      <>
                        <div className="relative">
                          <button
                            type="button"
                            className="border-0 bg-transparent p-0 cursor-pointer"
                            onClick={() => setStatusMenuOpenId(
                              statusMenuOpenId === signal.classifier_id ? '' : signal.classifier_id,
                            )}
                          >
                            <Badge status={signal.status} />
                          </button>
                          {statusMenuOpenId === signal.classifier_id && (
                            <StatusMenu
                              signal={signal}
                              onDeploy={handleDeploy}
                              onPause={handlePause}
                              onResume={handleResume}
                              onEdit={handleEdit}
                              onClose={() => setStatusMenuOpenId('')}
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          className="border-0 bg-transparent p-1 rounded cursor-pointer transition-colors"
                          style={{ color: palette.text.muted }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = palette.text.primary; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = palette.text.muted; }}
                          onClick={() => handleEdit(signal)}
                          title="Edit signal"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          className="border-0 bg-transparent p-1 rounded cursor-pointer transition-colors"
                          style={{ color: palette.text.muted }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = palette.status.danger; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = palette.text.muted; }}
                          onClick={() => setConfirmDeleteId(signal.classifier_id)}
                          title="Delete signal"
                        >
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Traces */}
                  <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs font-medium transition-opacity hover:opacity-85"
                      style={{
                        borderColor: palette.borders.subtle,
                        color: palette.accents.primary.base,
                        backgroundColor: palette.neutrals.surfaceSubtle,
                      }}
                      onClick={() => handleViewAllTraces(signal)}
                      title="View all traces where this signal fired"
                    >
                      View <ExternalLink size={11} />
                    </button>
                  </div>
                </div>

                {/* Inline detail expansion */}
                {isSelected && (
                  <SignalDetailBody
                    signal={signal}
                    timeLabel={timeLabel}
                    detailTab={detailTab}
                    setDetailTab={setDetailTab}
                    detailListOpen={detailListOpen}
                    setDetailListOpen={setDetailListOpen}
                    detailResults={detailResults}
                    detailLoading={detailLoading}
                    detailPagination={detailPagination}
                    detailPage={detailPage}
                    setDetailPage={setDetailPage}
                    detailSubtype={detailSubtype}
                    detailEventKey={detailEventKey}
                    setDetailSubtype={setDetailSubtype}
                    setDetailEventKey={setDetailEventKey}
                    detailDimensions={detailDimensions}
                    detailDimensionsLoading={detailDimensionsLoading}
                    labelItems={labelItems}
                    labelsLoading={labelsLoading}
                    labelsPagination={labelsPagination}
                    labelsPage={labelsPage}
                    setLabelsPage={setLabelsPage}
                    onOpenTrace={handleOpenTrace}
                    onFlag={setConfirmFlag}
                    onUnflag={setConfirmUnflag}
                    onEdit={handleEdit}
                    onDelete={setConfirmDeleteId}
                    onDeploy={handleDeploy}
                    onPause={handlePause}
                    onResume={handleResume}
                    actionLoading={actionLoading}
                  />
                )}
              </React.Fragment>
            );
          })
        ) : (
          <div className="py-16 text-center">
            <p className="text-sm mb-4" style={{ color: palette.text.supporting }}>
              No signals yet. Create one to start classifying interactions.
            </p>
            <button
              onClick={handleCreate}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg ${components.button.primary}`}
            >
              <Sparkles size={14} />
              New signal
            </button>
          </div>
        )}
      </TableFrame>

      {/* Confirm flag modal */}
      {confirmFlag && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: palette.quality.overlayModal }}
          onClick={() => { setConfirmFlag(null); setLabelType('ignore'); setCorrectedClassifier(''); }}
        >
          <div
            className="rounded-xl p-6 max-w-sm w-full mx-4"
            style={{
              backgroundColor: 'var(--color-dropdown-bg)',
              border: '1px solid var(--color-border-subtle)',
              boxShadow: 'var(--shadow-dropdown)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold mb-1" style={{ color: palette.text.primary }}>
              Remove from this signal?
            </h3>
            <p className="text-xs font-mono mb-4 truncate" style={{ color: palette.text.muted }}>
              {confirmFlag.trace_id?.substring(0, 16)}
            </p>

            {/* Label type selector */}
            <div className="flex gap-2 mb-4">
              {[
                { value: 'ignore', label: 'Ignore', desc: 'Just remove it' },
                { value: 'misclassified', label: 'Misclassified', desc: 'Belongs elsewhere' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setLabelType(opt.value); setCorrectedClassifier(''); }}
                  className="flex-1 px-3 py-2 rounded-lg text-xs text-left transition-colors"
                  style={{
                    backgroundColor: labelType === opt.value
                      ? withOpacity(palette.accents.primary.base, 15)
                      : palette.neutrals.surfaceSubtle,
                    border: `1px solid ${labelType === opt.value ? palette.accents.primary.base : palette.borders.subtle}`,
                    color: labelType === opt.value ? palette.accents.primary.base : palette.text.muted,
                  }}
                >
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-[10px] mt-0.5" style={{ color: palette.text.muted }}>{opt.desc}</div>
                </button>
              ))}
            </div>

            {/* Classifier picker for misclassified case */}
            {labelType === 'misclassified' && (
              <div className="mb-4">
                <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: palette.text.muted }}>
                  Which signal does it belong to?
                </p>
                <select
                  value={correctedClassifier}
                  onChange={(e) => setCorrectedClassifier(e.target.value)}
                  className="w-full px-3 py-2 rounded text-xs focus:outline-none"
                  style={{
                    backgroundColor: palette.neutrals.optionBg,
                    border: `1px solid ${palette.borders.subtle}`,
                    color: correctedClassifier ? palette.text.primary : palette.text.muted,
                  }}
                >
                  <option value="">Select a signal...</option>
                  {classifiers
                    .filter(c => c.classifier_key !== selectedSignal?.classifier_key)
                    .map(c => (
                      <option key={c.classifier_key} value={c.classifier_key}>{c.name}</option>
                    ))
                  }
                </select>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setConfirmFlag(null); setLabelType('ignore'); setCorrectedClassifier(''); }}
                className="px-3 py-1.5 text-xs rounded-md transition-colors"
                style={{ color: palette.text.supporting, backgroundColor: palette.neutrals.surfaceMid }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={labelType === 'misclassified' && !correctedClassifier}
                onClick={() => {
                  const result = { ...confirmFlag, _correctedClassifier: correctedClassifier || undefined };
                  handleFlag(result);
                  setConfirmFlag(null);
                  setLabelType('ignore');
                  setCorrectedClassifier('');
                }}
                className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                style={{
                  backgroundColor: labelType === 'misclassified' && !correctedClassifier
                    ? palette.neutrals.surfaceHover
                    : palette.status.warning,
                  color: labelType === 'misclassified' && !correctedClassifier ? palette.text.muted : palette.text.inverse,
                  cursor: labelType === 'misclassified' && !correctedClassifier ? 'not-allowed' : 'pointer',
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm unflag modal */}
      {confirmUnflag && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: palette.quality.overlayModal }}
          onClick={() => setConfirmUnflag(null)}
        >
          <div
            className="rounded-xl p-6 max-w-sm w-full mx-4"
            style={{
              backgroundColor: 'var(--color-dropdown-bg)',
              border: '1px solid var(--color-border-subtle)',
              boxShadow: 'var(--shadow-dropdown)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold mb-2" style={{ color: palette.text.primary }}>
              Move back to matches?
            </h3>
            <p className="text-xs mb-1" style={{ color: palette.text.supporting }}>
              This will remove the misclassified flag and restore the trace to the Matches tab.
            </p>
            <p className="text-xs font-mono mb-4 truncate" style={{ color: palette.text.muted }}>
              {confirmUnflag.trace_id?.substring(0, 16)}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmUnflag(null)}
                className="px-3 py-1.5 text-xs rounded-md transition-colors"
                style={{ color: palette.text.supporting, backgroundColor: palette.neutrals.surfaceMid }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = palette.neutrals.surfaceHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = palette.neutrals.surfaceMid; }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { handleUnflag(confirmUnflag); setConfirmUnflag(null); }}
                className={`px-3 py-1.5 text-xs rounded-md ${components.button.primary}`}
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0" style={{ backgroundColor: palette.quality.overlayModal }} onClick={() => setConfirmDeleteId(null)} />
          <div
            className="relative rounded-xl p-6 w-[360px] flex flex-col gap-4"
            style={{ backgroundColor: 'var(--color-card-bg)', border: '1px solid var(--color-border-subtle)' }}
          >
            <div>
              <p className="text-sm font-semibold mb-1" style={{ color: palette.text.primary }}>Delete signal?</p>
              <p className="text-xs" style={{ color: palette.text.muted }}>
                This signal will be permanently deleted — it will stop running on new traces and all past detections will be removed from your traces.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDeleteId(null)}
                className="px-3 py-1.5 text-xs rounded"
                style={{ border: '1px solid var(--color-border-subtle)', color: palette.text.muted, backgroundColor: 'var(--color-surface-hover)' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirmed}
                className="px-3 py-1.5 text-xs font-medium rounded"
                style={{ backgroundColor: palette.status.danger, color: palette.text.inverse }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wizard modal */}
      <ClassifierWizard
        isOpen={wizardOpen}
        onClose={() => { setWizardOpen(false); setEditingClassifier(null); }}
        onSave={handleSave}
        existingClassifier={editingClassifier}
        appId={appId}
        environment={environment}
        isDemo={isDemo}
      />
    </div>
  );
};

export default ClassifyTab;
